import React, { useEffect, useState, RefObject } from "react";

import { format } from "d3-format";
import { scaleLinear } from "d3-scale";

import { Status } from "../Status";
import { Text } from "../Text";

export const ACTION = {
  UPDATE_ITEM_PROP: "UPDATE_ITEM_PROP",
  TOGGLE_RESIZE: "TOGGLE_RESIZE",
} as const;

export const StatusBarItemType = {
  ON_TRACK: "on_track",
  BEHIND: "behind",
  AT_RISK: "at_risk",
} as const;

export type StatusBarItemType =
  (typeof StatusBarItemType)[keyof typeof StatusBarItemType];

export interface StatusBarDataItem {
  points: number;
  tag: StatusBarItemType;
  cumulative?: number;
  percent?: string;
  total?: number;
}

export interface PercentData extends StatusBarDataItem {
  cumulative: number;
  percent: string;
  total: number;
}

export interface ElementPosition extends PercentData {
  x1: number;
  x2: number;
  width: number;
}

interface TooltipProps {
  data: Array<{
    percent: string;
    points: number;
    total: number;
    tag: StatusBarItemType;
  }>;
  tooltips?: {
    [key in StatusBarItemType]?: {
      status?: string;
      text?: string;
    };
  };
}

interface Dimensions {
  width: number | null;
  height: number | null;
}

export const calculatePercents = (
  d: StatusBarDataItem[],
  total: number,
  formatter = format(".0f")
): PercentData[] => {
  const xScale = scaleLinear().domain([0, total]).range([0, 100]);

  const percent = (value: number): number => (value === 0 ? 0 : xScale(value));

  let acc = 0;

  const data = d
    .filter((f) => f.points !== 0 || f.tag === StatusBarItemType.AT_RISK)
    .map((item): PercentData => {
      acc += item.points;

      return {
        cumulative: acc - item.points,
        percent: formatter(percent(item.points)),
        total,
        ...item,
      };
    });
  return data;
};

export const totalData = (data: StatusBarDataItem[]): number =>
  data.reduce((acc, d) => acc + d.points, 0);

export const tooltipText = (tag: StatusBarItemType): string => {
  let r = "";
  switch (tag) {
    case "on_track":
      r = "On track";
      break;
    case "behind":
      r = "Behind";
      break;
    case "at_risk":
      r = "At risk";
      break;
  }
  return r;
};

export const Tooltip: React.FC<TooltipProps> = (props) => {
  const { percent, points, total, tag } = props.data[0];
  const tooltips = props?.tooltips;

  const currentTooltip = tooltips?.[tag];

  const currentTooltipStatus = currentTooltip?.status ?? tooltipText(tag);
  const currentTooltipText =
    currentTooltip?.text ??
    `${percent}% or ${points}/${total} Goals are ${tooltipText(
      tag
    ).toLowerCase()} of being completed on time.`;

  const statusMap: Record<StatusBarItemType, string> = {
    on_track: "green",
    behind: "yellow",
    at_risk: "red",
  };

  const statusVariant = statusMap[tag] || "green";

  return (
    <div>
      <Status variant={statusVariant}>
        <Text size="extraSmall">
          <b>{currentTooltipStatus}</b>
        </Text>
      </Status>
      <Text size="extraSmall">{currentTooltipText}</Text>
    </div>
  );
};

export const useResizeObserver = (ref: RefObject<HTMLElement>): Dimensions => {
  const [dimensions, setDimensions] = useState<Dimensions>({
    width: null,
    height: null,
  });

  useEffect(() => {
    if (!ref.current) {
      return;
    }
    const observeTarget = ref.current;
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      });
    });
    resizeObserver.observe(observeTarget);
    return () => {
      resizeObserver.unobserve(observeTarget);
    };
  }, [ref]);
  return dimensions;
};

const largeNumberWidth = 22;
const largeNumberRangeStarts = 19;
const midNumberWidth = 20;
const midNumberRangeStarts = 9;
const smallNumberWidth = 15;

export const percentLineWidth = (value: number): number =>
  value > largeNumberRangeStarts
    ? largeNumberWidth
    : value > midNumberRangeStarts
    ? midNumberWidth
    : smallNumberWidth;

export const percentsPosition = (
  percents: PercentData[],
  percentScale: (cumulative: number, points: number, total: number) => number
): ElementPosition[] => {
  const percentsNoZeros = percents.filter((v) => v.percent !== "0");
  const scalePositionsBeforeOverlapCheck = percentsNoZeros.map(
    (p): ElementPosition => {
      const cumulative = p.cumulative ?? 0;
      const total = p.total ?? 0;
      return {
        x1: percentScale(cumulative, p.points, total),
        x2:
          percentScale(cumulative, p.points, total) +
          percentLineWidth(Number(p.percent)),
        width: percentLineWidth(Number(p.percent)),
        ...p,
      };
    }
  );

  let timesOverlap = 0;
  const totalPercents = scalePositionsBeforeOverlapCheck.length;
  const scalePositionsAfterOverlapCheck = scalePositionsBeforeOverlapCheck.map(
    (s, i, a) => {
      // Check if it's the first percent of list, if it is
      // then we return the values initially calculated
      if (i === 0) {
        return {
          ...s,
          x1: s.x1,
          x2: s.x2,
        };
      }
      const offSet = 3;
      const percentTextWidth = 15;
      const previousPosition = a[i - 1];
      const nextPosition = a[i + 1];
      const minPercentForLeftOverlap = 90;

      // If at risk percent overlaps with previous values
      // and at risk percent is too high and there's a
      // overlap with values in left
      if (
        i + 1 === totalPercents &&
        s.x1 <= previousPosition.x2 &&
        Number(previousPosition.percent) > 0 &&
        Number(s.percent) > minPercentForLeftOverlap
      ) {
        timesOverlap++;
        return {
          ...s,
          x1: previousPosition.x2 + (timesOverlap - 1) * percentTextWidth,
          x2:
            previousPosition.x2 +
            s.width +
            (timesOverlap - 1) * percentTextWidth,
        };
      }

      const maxPercentForRightOverlap = 5;
      const minPercentForRightOverlap = 2;

      // Check if it's the last percent of list, if it is,
      // then we return the values calculated positions
      // considering right overlap
      if (
        (i + 1 === totalPercents &&
          Number(s.percent) < maxPercentForRightOverlap) ||
        (i + 1 === totalPercents &&
          s.x1 <= previousPosition.x2 &&
          Number(s.percent) > minPercentForRightOverlap)
      ) {
        return {
          ...s,
          x1: s.x1,
          x2: s.x2,
        };
      }

      // If overlaps with previous, then we take previous as base
      // and move the actual position to be after it
      if (s.x1 <= previousPosition.x2 && Number(previousPosition.percent) > 0) {
        timesOverlap++;
        return {
          ...s,
          x1: previousPosition.x2 + offSet,
          x2: previousPosition.x2 + s.width + offSet,
        };
      }

      // If overlaps with next and it's not the last,
      // then we take next as base
      // and move the actual position to be before it
      if (
        i + 1 !== totalPercents &&
        s.x2 >= nextPosition.x1 &&
        Number(nextPosition.percent) > 0
      ) {
        return {
          ...s,
          x1: nextPosition.x1 - offSet - s.width,
          x2: nextPosition.x1 - offSet,
        };
      }

      // If there's no overlapping then we keep original position
      return {
        ...s,
        x1: s.x1,
        x2: s.x2,
      };
    }
  );
  return scalePositionsAfterOverlapCheck;
};
